// SearchBox.tsx
"use client";

import React, { useState, useRef } from 'react';
import { SearchLinkWithPrice } from './searchElements/SearchLinkWithPrice';
import styles from './SearchBox.module.scss';

interface SearchBoxProps {
  isStocks?: boolean;
}

export default function SearchBox({ isStocks = false }: SearchBoxProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(!!value);
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    if (value.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    timeoutRef.current = setTimeout(async () => {
      try {
        const url = `/tkn_lnd_pg_api/search-tokens/?query=${encodeURIComponent(value)}&isStock=${isStocks}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setResults(Array.isArray(data.tokens) ? data.tokens.slice(0, 20) : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleBlur = () => {
    setTimeout(() => setShowResults(false), 2000);
  };

  const handleReset = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  };

  // Remove the early return that hides the dropdown
  // if (!showResults || (!results.length && !loading)) { ... }

  return (
    <div className={styles['searchbox-wrapper']}>
      <form className={styles['searchbox-form']} autoComplete="off" onSubmit={e => e.preventDefault()}>
        <div className={styles['searchbox-input-wrapper']}>
          <input
            ref={inputRef}
            type="text"
            id="searchbox-input"
            required
            placeholder=" "
            className={styles['searchbox-input']}
            value={query}
            onChange={handleChange}
            onFocus={() => setShowResults(!!query)}
            onBlur={handleBlur}
            autoComplete="off"
          />
          <label htmlFor="searchbox-input" className={styles['searchbox-label']}>Search {isStocks ? 'stocks' : 'tokens'} by name</label>
          <button
            type="button"
            className={
              styles['searchbox-reset-btn'] + (query ? ' ' + styles['searchbox-reset-btn--visible'] : '')
            }
            aria-label="Reset search"
            tabIndex={query ? 0 : -1}
            style={{ pointerEvents: query ? 'auto' : 'none' }}
            onClick={handleReset}
          >
            ×
          </button>
          {loading && (
            <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
              <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-white/60"></span>
            </span>
          )}
        </div>
        {showResults && query && (
          <div
            className={
              styles['searchbox-dropdown'] +
              ' rounded-b shadow-lg' +
              (showResults ? ' ' + styles['searchbox-dropdown--visible'] : '')
            }
          >
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : results.length === 0 ? (
              <div className="p-2 text-center text-gray-500">No results found</div>
            ) : (
              results.map((token, index) => (
                <SearchLinkWithPrice key={token.mint} token={token} index={index} isStocks={isStocks} />
              ))
            )}
          </div>
        )}
      </form>
    </div>
  );
}
